import { useState, useRef } from "react";
import { toast } from "sonner";
import { X, Upload, Trash2, Plus, ChevronLeft, ChevronRight } from "lucide-react";
import { api, mediaUrl, CATEGORIES } from "@/lib/api";

const empty = {
  brand: "", handle: "", note: "", link: "", layout: "grid", categories: ["Social Media"], posts: [], order: 0,
};

const field = "w-full bg-transparent border border-border rounded-lg focus:border-accent outline-none px-4 py-3 text-ink transition-colors";
const lbl = "text-xs uppercase tracking-widest text-muted2 block mb-2";

const LAYOUT_OPTS = [
  { v: "grid", label: "Instagram grid (3-col squares)" },
  { v: "posts", label: "Posts (larger tiles)" },
  { v: "reels", label: "Reels (vertical videos)" },
  { v: "masonry", label: "Masonry (mixed heights)" },
];

export default function FeedForm({ initial, onClose, onSaved }) {
  const [form, setForm] = useState(initial ? { ...empty, ...initial, categories: (initial.categories && initial.categories.length) ? initial.categories : ["Social Media"] } : empty);
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);
  const postsRef = useRef();

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const toggleCat = (cat) =>
    setForm((f) => {
      const has = f.categories.includes(cat);
      return { ...f, categories: has ? f.categories.filter((x) => x !== cat) : [...f.categories, cat] };
    });

  const uploadFile = async (file) => {
    const fd = new FormData();
    fd.append("file", file);
    const { data } = await api.post("/admin/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
    return data;
  };

  const onPosts = async (e) => {
    const files = Array.from(e.target.files);
    if (!files.length) return;
    setUploading(true);
    try {
      const uploaded = [];
      for (const f of files) { const d = await uploadFile(f); uploaded.push({ type: d.type, url: d.url, poster: "", caption: "" }); }
      set("posts", [...form.posts, ...uploaded]);
      toast.success(`${uploaded.length} added`);
    } catch { toast.error("Upload failed"); } finally { setUploading(false); }
  };

  const removePost = (i) => set("posts", form.posts.filter((_, idx) => idx !== i));
  const setPostField = (i, key, val) => set("posts", form.posts.map((m, idx) => (idx === i ? { ...m, [key]: val } : m)));
  const movePost = (i, dir) => {
    const t = i + dir;
    if (t < 0 || t >= form.posts.length) return;
    const g = [...form.posts];
    [g[i], g[t]] = [g[t], g[i]];
    set("posts", g);
  };

  const save = async () => {
    if (!form.brand.trim()) return toast.error("Brand name is required");
    if (!form.categories.length) return toast.error("Pick at least one category");
    setSaving(true);
    const payload = { ...form, order: Number(form.order) || 0 };
    try {
      if (initial?.id) await api.put(`/admin/feeds/${initial.id}`, payload);
      else await api.post("/admin/feeds", payload);
      toast.success("Feed saved");
      onSaved();
    } catch { toast.error("Save failed"); } finally { setSaving(false); }
  };

  return (
    <div className="fixed inset-0 z-[100] bg-bg/80 backdrop-blur-sm flex items-start justify-center overflow-y-auto overscroll-contain p-4 md:p-8" data-testid="feed-form-modal" data-lenis-prevent>
      <div className="w-full max-w-3xl rounded-xl border border-border bg-surface my-4">
        <div className="flex items-center justify-between p-6 border-b border-border sticky top-0 bg-surface z-10">
          <h2 className="font-serif text-2xl">{initial?.id ? "Edit feed" : "New social feed"}</h2>
          <button onClick={onClose} data-testid="close-feed-form" className="text-muted2 hover:text-ink"><X /></button>
        </div>

        <div className="p-6 space-y-5">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div><label className={lbl}>Brand name *</label><input data-testid="ff-brand" className={field} value={form.brand} onChange={(e) => set("brand", e.target.value)} placeholder="e.g. Marigold Cafe" /></div>
            <div><label className={lbl}>Handle</label><input data-testid="ff-handle" className={field} value={form.handle} onChange={(e) => set("handle", e.target.value)} placeholder="@brand" /></div>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div><label className={lbl}>Instagram / profile link</label><input data-testid="ff-link" className={field} value={form.link} onChange={(e) => set("link", e.target.value)} placeholder="https://instagram.com/…" /></div>
            <div><label className={lbl}>Order</label><input data-testid="ff-order" type="number" className={field} value={form.order} onChange={(e) => set("order", e.target.value)} /></div>
          </div>
          <div><label className={lbl}>Short note</label><textarea rows={2} className={field} value={form.note} onChange={(e) => set("note", e.target.value)} placeholder="A line about this short gig…" /></div>

          <div>
            <label className={lbl}>Layout</label>
            <div className="flex flex-wrap gap-2" data-testid="ff-layout">
              {LAYOUT_OPTS.map((o) => (
                <button
                  key={o.v} type="button" onClick={() => set("layout", o.v)}
                  data-testid={`ff-layout-${o.v}`}
                  className={`rounded-full px-3 py-1.5 text-xs font-mono uppercase tracking-wide border transition-colors ${
                    form.layout === o.v ? "bg-lime text-bg border-lime" : "border-border text-muted2 hover:text-ink hover:border-ink"
                  }`}
                >
                  {o.label}
                </button>
              ))}
            </div>
          </div>

          <div>
            <label className={lbl}>Show under (vinyl categories)</label>
            <div className="flex flex-wrap gap-2" data-testid="ff-categories">
              {CATEGORIES.map((cat) => (
                <button
                  key={cat} type="button" onClick={() => toggleCat(cat)}
                  data-testid={`ff-cat-${cat.toLowerCase().replace(/[^a-z]/g, "-")}`}
                  className={`rounded-full px-3 py-1.5 text-xs font-mono uppercase tracking-wide border transition-colors ${
                    form.categories.includes(cat) ? "bg-lime text-bg border-lime" : "border-border text-muted2 hover:text-ink hover:border-ink"
                  }`}
                >
                  {cat}
                </button>
              ))}
            </div>
            <p className="text-muted2 text-xs mt-2">This feed appears under each selected filter on the Work page.</p>
          </div>

          <div>
            <label className={lbl + " mb-2"}>Posts (images & videos)</label>
            <p className="text-muted2 text-xs mb-3">Use ‹ › to reorder, order here is exactly how they appear.</p>
            <div className="grid grid-cols-3 md:grid-cols-4 gap-3 mb-3">
              {form.posts.map((m, i) => (
                <div key={i} className="space-y-1">
                  <div className="relative group rounded-lg overflow-hidden border border-border aspect-square bg-bg flex items-center justify-center">
                    {m.type === "video"
                      ? <video src={mediaUrl(m.url)} className="w-full h-full object-cover" />
                      : <img src={mediaUrl(m.url)} alt="" className="w-full h-full object-cover" />}
                    <div className="absolute inset-x-0 bottom-0 flex justify-between p-1 opacity-0 group-hover:opacity-100 bg-gradient-to-t from-bg/90 to-transparent">
                      <button onClick={() => movePost(i, -1)} disabled={i === 0} className="bg-bg/80 rounded-full p-1 text-ink disabled:opacity-30" data-testid={`ff-post-left-${i}`}><ChevronLeft size={13} /></button>
                      <button onClick={() => movePost(i, 1)} disabled={i === form.posts.length - 1} className="bg-bg/80 rounded-full p-1 text-ink disabled:opacity-30" data-testid={`ff-post-right-${i}`}><ChevronRight size={13} /></button>
                    </div>
                    <button onClick={() => removePost(i)} className="absolute top-1 right-1 bg-bg/80 rounded-full p-1 text-ink opacity-0 group-hover:opacity-100" data-testid={`ff-post-remove-${i}`}><Trash2 size={13} /></button>
                  </div>
                  <input
                    value={m.caption || ""}
                    onChange={(e) => setPostField(i, "caption", e.target.value)}
                    placeholder="Caption (optional)"
                    data-testid={`ff-post-caption-${i}`}
                    className="w-full bg-transparent border border-border rounded px-2 py-1 text-[11px] text-ink focus:border-accent outline-none"
                  />
                </div>
              ))}
              <button onClick={() => postsRef.current.click()} className="rounded-lg border border-dashed border-border aspect-square flex items-center justify-center text-muted2 hover:border-accent hover:text-accent" data-testid="ff-posts-add"><Plus /></button>
            </div>
            <input ref={postsRef} type="file" accept="image/*,video/*" multiple hidden onChange={onPosts} />
          </div>
        </div>

        <div className="flex items-center justify-end gap-3 p-6 border-t border-border sticky bottom-0 bg-surface">
          {uploading && <span className="text-muted2 text-sm mr-auto">Uploading…</span>}
          <button onClick={onClose} className="rounded-full border border-border px-6 py-3 text-sm hover:border-ink">Cancel</button>
          <button onClick={save} disabled={saving} data-testid="ff-save" className="rounded-full bg-accent text-bg px-6 py-3 text-sm font-medium hover:bg-accent-hover disabled:opacity-60">{saving ? "Saving…" : "Save feed"}</button>
        </div>
      </div>
    </div>
  );
}
